import { Dialog } from '@headlessui/react';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useState } from 'react';

interface ModalProps {
  allowClosing?: boolean;
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  size?: string;
}

const Modal = ({
  allowClosing = true,
  children,
  className = '',
  isOpen: defaultIsOpen = false,
  onClose = () => null,
  onOpen = () => null,
  size = 'small',
}: ModalProps) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const handleOnClose = () => {
    onClose();
    setIsOpen(false);
  };

  const handleOnOpen = () => {
    onOpen();
    setIsOpen(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: we should not memoize function props
  useEffect(() => {
    if ((defaultIsOpen && isOpen) || defaultIsOpen !== isOpen) {
      defaultIsOpen ? handleOnOpen() : handleOnClose();
    }
  }, [defaultIsOpen, isOpen]);

  return (
    <Dialog
      className="Modal-container"
      open={isOpen}
      onClose={() =>
        allowClosing
          ? handleOnClose()
          : () => {
              // do nothing
            }
      }
    >
      <div
        className={classNames(`Modal Modal--${size}`, className)}
        data-testid="Modal"
      >
        <Dialog.Panel className="Modal-dialog">
          {allowClosing && (
            <button
              className="Modal-closeButton"
              type="button"
              onClick={handleOnClose}
              aria-label="Close"
              data-testid="closeModalBtn"
            >
              <span className="Icon Icon--close Icon--xs margin-0" />
            </button>
          )}
          <div className="Modal-content" data-testid="modalContent">
            {children}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export { Modal };
